import addMinutes from 'date-fns/addMinutes';
import isBefore from 'date-fns/isBefore';
import * as Rx from 'rxjs';
import { debounceTime, filter, map, tap, concat } from 'rxjs/operators';

import storageService from './storage';
import type CacheProps from '../interfaces/cache';

class CacheService {
  private change$ = new Rx.Subject<{ key: string; value: CacheProps }>();
  private memory: { [key: string]: CacheProps };

  constructor() {
    this.memory = {};
  }

  public getData<T = any>(key: string): Rx.Observable<CacheProps<T>> {
    if (this.memory[key]) return Rx.of(this.memory[key]);
    return storageService.get('myedz-cache-' + key);
  }

  public watchData<T>(key: string): Rx.Observable<CacheProps<T>> {
    return this.getData<T>(key).pipe(
      concat(
        this.change$.pipe(
          filter(data => data.key === key),
          map(data => data.value)
        )
      ),
      debounceTime(100)
    );
  }

  public removeData(key: string) {
    return storageService.set('myedz-cache-' + key, null).pipe(
      tap(() => (this.memory[key] = null)),
      tap(() => this.change$.next({ key, value: null }))
    );
  }

  public saveData<T>(
    key: string,
    data: T,
    options: { persist: boolean; expirationMinutes: number } = {
      persist: false,
      expirationMinutes: 5
    }
  ): Rx.Observable<CacheProps<T>> {
    const cache: CacheProps<T> = {
      createdAt: new Date(),
      expirationDate: addMinutes(new Date(), options.expirationMinutes),
      data
    };

    if (options.persist) {
      return storageService.set('myedz-cache-' + key, cache).pipe(tap(() => this.change$.next({ key, value: cache })));
    }

    return Rx.of(true).pipe(
      map(() => {
        this.memory[key] = cache;
        this.change$.next({ key, value: cache });
        return cache;
      })
    );
  }

  public isExpirated(cache: CacheProps): boolean {
    return isBefore(cache.expirationDate, new Date());
  }

  public clear(): Rx.Observable<void> {
    return storageService.clear(/^myedz-cache-/gi).pipe(tap(() => (this.memory = {})));
  }
}

const cacheService = new CacheService();
export default cacheService;
